import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type * as variableOverrides from '../../../../../../variable-overrides'
import type * as currentRendering from '../../../feature'
import type { Feature } from '../feature'

/**
 * Triggers a page rendering request, when the current rendering feature renders successfully.
 */
export const triggerPageRendering = store.effects.create(
  (
    feature: Feature,
    currentRenderingFeature: currentRendering.Feature,
    variablesFeature: variableOverrides.Feature,
  ) =>
    currentRenderingFeature.actions.render.success.observe().pipe(
      // We use withLatestFrom since both features share the same context,
      // avoiding unnecessary reactions to context changes, and at this point
      // (currentRenderingFeature.actions.render.success) context has been set
      rxjs.withLatestFrom(
        currentRenderingFeature.selectors.context.asObservable,
      ),
      rxjs.switchMap(
        ([
          {
            data: {
              response: { hasQuery },
            },
          },
          pageRenderingContext,
        ]) => {
          // Skip processing and terminate stream if pageContext is missing
          // or if rendering doesn't need a query

          if (!pageRenderingContext || !hasQuery) {
            return rxjs.EMPTY
          }

          const { pageId, scenarioId, appId } = pageRenderingContext

          return rxjs.of(pageRenderingContext).pipe(
            // Using withLatestFrom because both pageRendering and currentRenderingFeature
            // share variables. Changes in currentRenderingFeature variables trigger
            // a new rendering success request
            rxjs.withLatestFrom(
              variablesFeature.selectors.getVariableOverrides({
                page: {
                  appId,
                  pageId,
                  scenarioId,
                },
              }).asObservable,
            ),
          )
        },
      ),
      rxjs.map(([pageRenderingContext, pageVariables]) =>
        feature.actions.render.request
          .from('triggerPageRendering (effect)')
          .create({
            request: { ...pageRenderingContext, variables: pageVariables },
          }),
      ),
    ),
)
